import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, shareReplay } from 'rxjs';
import { environment } from 'src/environments/environment';
const DEFAULT_DOMAIN = '';

/** Faculty Referral Form — feedback category for questions / save */
export const FACULTY_REFERRAL_CATEGORY = 'Faculty Referral Form';

/** searchType for saveCache (Swagger uses "Student"). */
export const STUDENT_SEARCH_TYPE = 'Student' as const;
/** searchType for GET searchCacheDetails (recent students list). */
export const SEARCH_CACHE_DETAILS_TYPE = 'STUDENT' as const;
export type StudentSearchType = typeof STUDENT_SEARCH_TYPE | typeof SEARCH_CACHE_DETAILS_TYPE;
export type StudentBusinessType = 'COLLEGE';


/** Row from search cache / college student search APIs */
export interface StudentSearchRowDto {
  admissionNo?: string;
  admNo?: number | string;
  admNO?: string;
  encryptedAdmNo?: string;
  sectionId?: number;
  secId?: number;
  searchSelected?: string;
  studentName?: string;
  name?: string;
  searchKey?: string;
  searchName?: string;
  searchType?: string;
  parentName?: string;
  fatherName?: string;
  courseName?: string;
  className?: string;
  sectionName?: string;
  programName?: string;
  streamName?: string;
  campusName?: string;
  buildingName?: string;
  boardingType?: string;
  studentStatus?: string;
  status?: string;
  stdUniqueRefId?: string;
  uniqueRefId?: string;
  stdName?: string;
  surname?: string;
  courseTrackName?: string;
  studentType?: string;
  mobileNo?: number | string;
  favourite?: boolean;
  businessType?: string;
  [key: string]: unknown;
}

/** POST …/college/studentSearch request body */
export interface CollegeStudentSearchRequest {
  stdName: string;
  admNo: number;
  parentName: string;
  mobileNo: number;
  pageCount: number;
  searchKey: string;
  businessType: string;
}
export interface FacultyReferralFeedbackHistoryItem {
  questionId: number;
  optionId: number;
  feedbackOptionText?: string;
  feedbackId?: number;
  feedbackPopUpText?: string;
}
export interface FacultyReferralFeedbackHistoryEntry {
  feedbackDate?: string;
  feedBackDate?: string;
  feedBackHistory?: FacultyReferralFeedbackHistoryItem[];
}

/** Input for {@link StudentSearchService.searchCollegeStudents} (partial + legacy searchCriteria). */
export type CollegeStudentSearchParams = Partial<
  Omit<CollegeStudentSearchRequest, 'admNo'>
> & {
  searchCriteria?: string;
  /** Admission number as digits or string (converted to number in request body). */
  admNo?: number | string;
};

export interface StudentSearchCacheSaveParams {
  searchKey: string;
  searchSelected: string;
  searchType?: StudentSearchType;
  businessType?: StudentBusinessType;
  parentName?: string;
  favourite?: boolean;
  stdUniqueRefId?: string;
  /** When true (default if stdUniqueRefId is set), append stdUniqueRefId — required by employee app. */
  includeStdUniqueRefId?: boolean;
}

export interface FacultyReferralQuestion {
  questionId: number;
  question: string;
  teluguQuestion?: string;
  questionType?: string;
  questionTitle?: string;
}

export interface FacultyReferralOption {
  optionId: number;
  optionName: string;
  teluguOptionName?: string;
  optionRank: number;
  optionRankStatus?: boolean;
}

/** Flat row — matches Swagger saveStdFeedbackDetails body */
export interface FacultyReferralSaveRow {
  questionId: number;
  optionId: number;
  feedbackOptionText: string;
  feedbackPopUpText: string;
  feedbackId: string | number;
}

export interface FacultyReferralSavePayload {
  admNO: string;
  empId: string | number;
  feedBackType: string;
  category: string;
  sectionId: number;
  questionAndOption: FacultyReferralSaveRow[];
}

export interface FacultyReferralFeedbackDetailsResponse {
  admNO?: string;
  admNo?: string;
  empId?: string;
  sectionId?: number;
  category?: string;
  feedBackType?: string;
  questionAndOption?: FacultyReferralSaveRow[] | Record<string, unknown>[];
  [key: string]: unknown;
}

export interface StudentProfileInfoResponse {
  studentName?: string;
  name?: string;
  admissionNo?: string | number;
  admNo?: string | number;
  campusName?: string;
  campus?: string;
  studentType?: string;
  boardingType?: string;
  sectionId?: number | string;
  secId?: number | string;
  photoUrl?: string;
  imageUrl?: string;
  [key: string]: unknown;
}

export interface EmployeeState {
  stateId: number;
  stateName: string;
}

export interface EmployeeCity {
  cityId: number;
  cityName: string;
}

export interface EmployeeCampus {
  campusId: number;
  campusName: string;
}

export interface FeedBackQuestionAndOptionApi {
  questionId: number;
  question: string;
  teluguQuestion?: string;
  questionType?: string;
  questionTitle?: string;
  options: FacultyReferralOption[];
}

export interface WellBeingReferralReportOption {
  optionId?: number;
  feedbackId?: number;
  status?: boolean;
  feedbackOptionText?: string;
  feedbackPopUpText?: string;
  option?: string;
  optionRank?: number;
}

export interface WellBeingReferralReportQuestion {
  questionId: number;
  question?: string;
  options: WellBeingReferralReportOption[];
}

export interface WellBeingReferralReportRow {
  feedBackDate: string;
  admNo: string;
  studentName?: string;
  sectionName?: string;
  campusName?: string;
  questionAndOption: WellBeingReferralReportQuestion[];
}

export interface WellBeingReferralReportPage {
  data: WellBeingReferralReportRow[];
  totalRecords: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

const DEFAULT_STD_FEEDBACK_TYPE = 'EMPLOYEE_FEEDBACK';

@Injectable({
  providedIn: 'root'
})
export class FacultyReferralFormService {

private readonly API_ORIGIN = String((environment as any).masterDomain || DEFAULT_DOMAIN)
    .trim()
    .replace(/\/+$/, '');

  private readonly searchCacheDetailsUrl = `${this.API_ORIGIN}/employee/api/jwtV2/student/search/searchCacheDetails`;
  private readonly saveCacheUrl = `${this.API_ORIGIN}/employee/api/jwtV2/student/search/saveCache`;
  private readonly collegeStudentSearchUrl = `${this.API_ORIGIN}/employee/api/jwtV2/college/studentSearch`;
  private readonly feedbackQuestionsUrl = `${this.API_ORIGIN}/employee/api/jwtV2/feedBackQuestions`;
  private readonly feedbackOptionsUrl = `${this.API_ORIGIN}/employee/api/jwtV2/feedBackOptions`;
  private readonly feedbackDetailsUrl = `${this.API_ORIGIN}/employee/api/jwtV2/empFeedBack/feedbackDetails`;
  private readonly saveStdFeedbackUrl = `${this.API_ORIGIN}/employee/api/jwtV2/empFeedBack/saveStdFeedbackDetails`;
  private readonly studentProfileUrl = `${this.API_ORIGIN}/employee/api/jwtV2/stdDashboard/empApp/getStudentProfileInfo`;
    private readonly feedbackHistoryUrl = `${this.API_ORIGIN}/employee/api/jwtV2/empFeedBack/getfeedbackHistory`;
  private readonly studentUploadImageUrl = `${this.API_ORIGIN}/employee/api/jwtV2/student/getUploadImage`;
  private readonly feedbackQuestionAndOptionsUrl = `${this.API_ORIGIN}/employee/api/jwtV2/feedBackQuestionAndOptions`;
  private readonly recentFeedBackReportUrl = `${this.API_ORIGIN}/employee/api/jwtV2/empFeedBack/getRecentFeedBackReport`;
  private readonly wellBeingReferralReportUrl = `${this.API_ORIGIN}/employee/api/jwtV2/empFeedBack/wellBeingReferralReport`;

  private readonly payrollStorageKeys = ['payrollId', 'payRollId', 'employeePayrollId', 'userPayrollId'] as const;
  private readonly empIdStorageKeys = [
    'empId',
    'employeeId',
    'employeePayrollId',
    'payrollId',
    'payRollId',
    'userPayrollId'
  ] as const;

  private readonly cachedOptionsByQuestionId = new Map<number, Observable<FacultyReferralOption[]>>();

  constructor(private readonly http: HttpClient) {
   
  }
    
  private get headers(): HttpHeaders {
    return new HttpHeaders({
      'Authorization': this.authHeaderValue,
      'Content-Type': 'application/json'
    });
  }

 
  private readonly tokenStorageKeys = ['updated', 'jwtToken', 'token', 'authToken', 'accessToken'] as const;

  private get authHeaderValue(): string {
    let raw: string | null = null;
    for (const key of this.tokenStorageKeys) {
      raw = sessionStorage.getItem(key) || localStorage.getItem(key);
      if (raw && raw.trim()) break;
    }
    const trimmed = (raw || '').trim();
    if (trimmed) return trimmed;
    return '';
  }

  getStoredPayrollId(): any {
    for (const key of this.payrollStorageKeys) {
      const v = localStorage.getItem(key) || sessionStorage.getItem(key);
      if (v?.trim()) return String(v).trim();
    }
  
  }

  /** Employee id for saveStdFeedbackDetails (plain or encrypted from storage). */
  getStoredEmpId(): any{
    for (const key of this.empIdStorageKeys) {
      const v = localStorage.getItem(key) || sessionStorage.getItem(key);
      if (v?.trim()) return String(v).trim();
    }
   
  }
  isApiAccessDenied(res: unknown): boolean {
  if (res == null) return false;
  if (typeof res === 'string') {
    return res.toLowerCase().includes('access denied');
  }
  if (typeof res !== 'object') return false;
  const o = res as Record<string, unknown>;
  const err = String(o['error'] ?? o['message'] ?? o['status'] ?? '').toLowerCase();
  return err.includes('access denied') || err === 'denied';
}
  /** Recent / cached students for faculty referral list */
  getSearchCacheDetails(
    searchType: StudentSearchType = SEARCH_CACHE_DETAILS_TYPE
  ): Observable<StudentSearchRowDto[]> {
    const params = new HttpParams().set('searchType', searchType);
    return this.http
      .get<unknown>(this.searchCacheDetailsUrl, { headers: this.headers, params })
      .pipe(
        map((res) => this.unwrapStudentList(this.assertNotAccessDenied(res)))
      );
  }

  /**
   * POST …/student/search/saveCache (Swagger — query params + empty body, Bearer JWT).
   * searchKey & searchSelected & searchType & favourite & businessType & parentName
   */
  saveSearchCache(params: StudentSearchCacheSaveParams): Observable<unknown> {
    const url = this.buildSaveCacheUrl(params);
    return this.http
      .post<unknown>(url, null, { headers: this.headers })
      .pipe(map((res) => this.assertNotAccessDenied(res)));
  }

  /** Query string — same order as Swagger saveCache. */
  buildSaveCacheUrl(params: StudentSearchCacheSaveParams): string {
    const enc = encodeURIComponent;
    const searchKey = String(params.searchKey ?? '').trim();
    const searchSelected = String(params.searchSelected ?? '').trim();
    const ref = String(params.stdUniqueRefId ?? '').trim();
    const includeRef = params.includeStdUniqueRefId === true && !!ref;

    const parts = [
      `searchKey=${enc(searchKey)}`,
      `searchSelected=${enc(searchSelected)}`,
      `searchType=${enc(params.searchType ?? STUDENT_SEARCH_TYPE)}`,
      `favourite=${enc(String(params.favourite ?? false))}`,
      `businessType=${enc(params.businessType ?? 'COLLEGE')}`
    ];

    const parent = String(params.parentName ?? '').trim();
    if (parent) {
      parts.push(`parentName=${enc(parent)}`);
    }

    if (includeRef) {
      parts.push(`stdUniqueRefId=${enc(ref)}`);
    }

    return `${this.saveCacheUrl}?${parts.join('&')}`;
  }

  private assertNotAccessDenied<T>(res: T): T {
    if (this.isApiAccessDenied(res)) {
      throw Object.assign(new Error('Access Denied'), { status: 403, body: res });
    }
    return res;
  }

  /** Search students — POST …/college/studentSearch */
  searchCollegeStudents(params: CollegeStudentSearchParams): Observable<StudentSearchRowDto[]> {
    const payload = this.buildCollegeStudentSearchBody(params);
    return this.http
      .post<unknown>(this.collegeStudentSearchUrl, payload, { headers: this.headers })
      .pipe(
        map((res) => this.unwrapStudentList(this.assertNotAccessDenied(res)))
      );
  }

  /** Search before opening evaluation (full student fields from list row). */
  searchCollegeStudentByRecord(picked: {
    admissionNo: string;
    name: string;
    parentName?: string;
    mobileNo?: number;
  }): Observable<StudentSearchRowDto[]> {
    return this.searchCollegeStudents({
      searchKey: picked.admissionNo.trim(),
      stdName: picked.name.trim(),
      parentName: String(picked.parentName ?? '').trim(),
      admNo: this.toAdmNoNumber(picked.admissionNo),
      mobileNo: picked.mobileNo ?? 0,
      pageCount: 0,
      businessType: 'COLLEGE'
    });
  }

  private buildCollegeStudentSearchBody(
    params: CollegeStudentSearchParams
  ): CollegeStudentSearchRequest {
    const searchKey = String(params.searchKey ?? params.searchCriteria ?? '').trim();
    const stdName = String(params.stdName ?? '').trim();
    const parentName = String(params.parentName ?? '').trim();
    const admSource = params.admNo ?? searchKey;

    return {
      stdName,
      admNo: this.toAdmNoNumber(admSource),
      parentName,
      mobileNo: this.toNonNegativeInt(params.mobileNo),
      pageCount: this.toNonNegativeInt(params.pageCount),
      searchKey,
      businessType: String(params.businessType ?? 'COLLEGE').trim() || 'COLLEGE'
    };
  }

  private toAdmNoNumber(value: unknown): number {
    if (value == null || value === '') return 0;
    if (typeof value === 'number' && Number.isFinite(value)) return Math.trunc(value);
    const digits = String(value).replace(/\D/g, '');
    const n = Number(digits);
    return Number.isFinite(n) ? n : 0;
  }

  private toNonNegativeInt(value: unknown): number {
    const n = Number(value);
    return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : 0;
  }

  /** Evaluation questions — categoryName=Faculty Referral Form */
  getFacultyReferralQuestions(
    categoryName: string = FACULTY_REFERRAL_CATEGORY
  ): Observable<FacultyReferralQuestion[]> {
    const q = encodeURIComponent(categoryName.trim());
    return this.http
      .get<FacultyReferralQuestion[]>(`${this.feedbackQuestionsUrl}?categoryName=${q}`, {
        headers: this.headers
      })
      .pipe(map((list) => (Array.isArray(list) ? list : [])));
  }

  /** Options for one question — feedBackOptions?questionId= */
  getFacultyReferralOptions(questionId: number): Observable<FacultyReferralOption[]> {
    const params = new HttpParams().set('questionId', String(questionId));
    return this.http
      .get<FacultyReferralOption[]>(this.feedbackOptionsUrl, { headers: this.headers, params })
      .pipe(
        map((opts) =>
          [...(opts ?? [])].sort((a, b) => {
            const ra = Number(a.optionRank ?? 0);
            const rb = Number(b.optionRank ?? 0);
            if (ra !== rb) return ra - rb;
            return Number(a.optionId ?? 0) - Number(b.optionId ?? 0);
          })
        )
      );
  }

  getFacultyReferralOptionsCached(questionId: number): Observable<FacultyReferralOption[]> {
    const id = Number(questionId);
    if (!Number.isFinite(id)) return of([]);
    const existing = this.cachedOptionsByQuestionId.get(id);
    if (existing) return existing;
    const obs = this.getFacultyReferralOptions(id).pipe(
      catchError(() => of([])),
      shareReplay(1)
    );
    this.cachedOptionsByQuestionId.set(id, obs);
    return obs;
  }

  /**
   * Existing student feedback for faculty referral (history / prefill).
   * GET …/empFeedBack/feedbackDetails?admNo=…&category=Faculty%20Referral%20Form
   */
  getFacultyReferralFeedbackDetails(
    admNo: string,
    category: string = FACULTY_REFERRAL_CATEGORY
  ): Observable<FacultyReferralFeedbackDetailsResponse> {
    const adm = String(admNo ?? '').trim();
    const cat = encodeURIComponent(String(category ?? '').trim());
    const admParam = adm.includes('%') ? adm : encodeURIComponent(adm);
    const url = `${this.feedbackDetailsUrl}?admNo=${admParam}&category=${cat}`;
    return this.http.get<FacultyReferralFeedbackDetailsResponse>(url, { headers: this.headers });
  }

  /**
   * Student profile details for header (uses encrypted admNo token).
   * POST …/stdDashboard/empApp/getStudentProfileInfo?admNo=<token> (empty body)
   */
  getStudentProfileInfo(admNoToken: string): Observable<StudentProfileInfoResponse> {
    const raw = String(admNoToken ?? '').trim();
    if (!raw) return of({});

    const admParam = raw.includes('%') ? raw : encodeURIComponent(raw);
    const url = `${this.studentProfileUrl}?admNo=${admParam}`;

    return this.http
      .post<StudentProfileInfoResponse>(url, null,  { headers: this.headers })
      .pipe(
        map((res) =>
          this.unwrapProfileResponse(this.assertNotAccessDenied(res ?? ({} as StudentProfileInfoResponse)))
        )
      );
  }

  private unwrapProfileResponse(res: unknown): StudentProfileInfoResponse {
    if (!res || typeof res !== 'object') return {};
    const o = res as Record<string, unknown>;
    const nested = o['data'] ?? o['studentProfile'] ?? o['profile'] ?? o['result'];
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      return nested as StudentProfileInfoResponse;
    }
    return res as StudentProfileInfoResponse;
  }

  /** Submit faculty referral evaluation — POST …/empFeedBack/saveStdFeedbackDetails */
  saveFacultyReferralDetails(payload: FacultyReferralSavePayload): Observable<unknown> {
    const body: FacultyReferralSavePayload = {
      admNO: String(payload.admNO ?? '').trim(),
      empId: payload.empId,
      feedBackType: payload.feedBackType ?? DEFAULT_STD_FEEDBACK_TYPE,
      category: payload.category ?? FACULTY_REFERRAL_CATEGORY,
      sectionId: Number(payload.sectionId ?? 0),
      questionAndOption: payload.questionAndOption ?? []
    };
    return this.http
      .post(this.saveStdFeedbackUrl, body, {
        headers: this.headers,
        observe: 'response',
        responseType: 'text'
      })
      .pipe(
        map((res) => {
          const status = Number(res.status ?? 0);
          if (status === 200 || status === 204) {
            return { status: 'OK' };
          }
          throw Object.assign(new Error('Save failed'), { status });
        })
      );
  }

  /** Map college/studentSearch row or search-cache row to UI model. */
   mapRowToStudent(row: StudentSearchRowDto): MappedStudentSearchRow {
    const admissionNo = String(
      row.searchSelected ?? row.admNo ?? row.admissionNo ?? ''
    ).trim();

    let name = String(
      row.name ?? row.searchName ?? row.searchKey ?? row.stdName ?? row.studentName ?? ''
    ).trim();
    if (!name && row.surname) {
      name = String(row.surname).trim();
    }

    const parentName = String(row.parentName ?? row.fatherName ?? '').trim();
    const stdUniqueRefId = this.readStdUniqueRefId(row);
    const admNoToken = this.resolveAdmNoToken(row, admissionNo, stdUniqueRefId);

    const courseParts = [
      row.courseTrackName,
      row.className,
      row.sectionName,
      row.programName,
      row.streamName,
      row.courseName
    ]
      .map((x) => String(x ?? '').trim())
      .filter(Boolean);

    const sectionId = Number(row.sectionId ?? row.secId ?? 0);

    return {
      admissionNo,
      admNoToken,
      name,
      parentName,
      courseLine: courseParts.join(' | ') || '—',
      campus: String(row.campusName ?? row.buildingName ?? '').trim() || '—',
      boardingType: String(row.studentType ?? row.boardingType ?? '').trim() || '—',
      studentOrientation: this.readStudentOrientation(row),
      status: String(row.status ?? row.studentStatus ?? 'CURRENT').trim() || 'CURRENT',
      stdUniqueRefId,
      sectionId: Number.isFinite(sectionId) ? sectionId : 0,
      raw: row
    };
  }
    getStudentUploadImageBlob(admNoToken: string): Observable<Blob> {
    const adm = String(admNoToken ?? '').trim();
    if (!adm) return of(new Blob());

    const admParam = adm.includes('%') ? adm : encodeURIComponent(adm);
    const url = `${this.studentUploadImageUrl}?admNo=${admParam}`;
    // const imgHeaders = this.getHeaders.set(
    //   'Accept',
    //   'image/avif,image/webp,image/apng,image/jpeg,image/png,image/*,*/*;q=0.8'
    // );

    return this.http.get(url, { headers: this.headers, responseType: 'blob' }).pipe(
      catchError(() => of(new Blob()))
    );
  }

  /** Params for GET …/student/search/saveCache (matches Swagger / working employee app). */
  buildSaveCacheParams(student: MappedStudentSearchRow): StudentSearchCacheSaveParams {
    return {
      searchKey: student.name,
      searchSelected: student.admissionNo,
      searchType: STUDENT_SEARCH_TYPE,
      favourite: false,
      businessType: 'COLLEGE',
      parentName: student.parentName
    };
  }
 private readStudentOrientation(row: StudentSearchRowDto): string {
    const raw = row as Record<string, unknown>;
    return String(
      raw['studentOrienation'] ??
        raw['studentOrientation'] ??
        row.boardingType ??
        row.studentType ??
        ''
    ).trim();
  }
    getEmployeeStates(): Observable<EmployeeState[]> {
    const url = `${this.API_ORIGIN}/apiV2/common/employeeStates`;
    return this.http.get<EmployeeState[]>(url, { headers: this.headers });
  }

  getEmployeeCities(stateId: number): Observable<EmployeeCity[]> {
    const url = `${this.API_ORIGIN}/apiV2/common/emplStateCities?stateId=${stateId}`;
    return this.http.get<EmployeeCity[]>(url, { headers: this.headers });
  }

  getEmployeeCampuses(cityId: number): Observable<EmployeeCampus[]> {
    const url = `${this.API_ORIGIN}/apiV2/common/empCityCampuses?cityId=${cityId}`;
    return this.http.get<EmployeeCampus[]>(url, { headers: this.headers });
  }

  getFeedBackQuestionAndOptions(
    categoryName: string = FACULTY_REFERRAL_CATEGORY,
    feedBackType: string = DEFAULT_STD_FEEDBACK_TYPE
  ): Observable<FeedBackQuestionAndOptionApi[]> {
    const url =
      `${this.feedbackQuestionAndOptionsUrl}` +
      `?categoryName=${encodeURIComponent(categoryName)}` +
      `&feedBackType=${encodeURIComponent(feedBackType)}`;
    return this.http
      .get<FeedBackQuestionAndOptionApi[]>(url, { headers: this.headers })
      .pipe(map((res) => (Array.isArray(res) ? res : [])));
  }

  getWellBeingReferralReport(params: {
    category?: string;
    stateId?: number | null;
    cityId?: number | null;
    campusId?: number | null;
    fromDate?: string;
    toDate?: string;
    page?: number | null;
    pageSize?: number | null;
  }): Observable<WellBeingReferralReportPage> {
    const category = String(params.category ?? FACULTY_REFERRAL_CATEGORY).trim();
    let httpParams = new HttpParams().set('category', category);

    if (params.stateId != null && Number.isFinite(Number(params.stateId))) {
      httpParams = httpParams.set('stateId', String(params.stateId));
    }
    if (params.cityId != null && Number.isFinite(Number(params.cityId))) {
      httpParams = httpParams.set('cityId', String(params.cityId));
    }
    if (params.campusId != null && Number.isFinite(Number(params.campusId))) {
      httpParams = httpParams.set('campusId', String(params.campusId));
    }
    const fromDate = String(params.fromDate ?? '').trim();
    const toDate = String(params.toDate ?? '').trim();
    if (fromDate) {
      httpParams = httpParams.set('fromDate', fromDate);
    }
    if (toDate) {
      httpParams = httpParams.set('toDate', toDate);
    }

    const page = params.page != null && Number.isFinite(Number(params.page)) ? Number(params.page) : 0;
    const pageSize = params.pageSize != null && Number.isFinite(Number(params.pageSize)) ? Number(params.pageSize) : 50;
    httpParams = httpParams.set('page', String(page));
    httpParams = httpParams.set('pageSize', String(pageSize));

    return this.http
      .get<unknown>(this.wellBeingReferralReportUrl, {
        headers: this.headers,
        params: httpParams,
      })
      .pipe(map((res) => this.toWellBeingReportPage(res, page, pageSize)));
  }

  getRecentFeedBackReport(
    category: string = FACULTY_REFERRAL_CATEGORY,
  ): Observable<WellBeingReferralReportPage> {
    const httpParams = new HttpParams().set('category', String(category ?? '').trim());
    return this.http
      .get<unknown>(this.recentFeedBackReportUrl, {
        headers: this.headers,
        params: httpParams,
      })
      .pipe(map((res) => this.toWellBeingReportPage(res, 0, 0)));
  }

  private toWellBeingReportPage(
    res: unknown,
    requestedPage: number,
    requestedPageSize: number,
  ): WellBeingReferralReportPage {
    if (Array.isArray(res)) {
      const data = res as WellBeingReferralReportRow[];
      return {
        data,
        totalRecords: data.length,
        totalPages: 1,
        currentPage: 0,
        pageSize: data.length,
      };
    }

    const obj = (res ?? {}) as Record<string, unknown>;
    const data = Array.isArray(obj['data']) ? (obj['data'] as WellBeingReferralReportRow[]) : [];
    const toNum = (value: unknown, fallback: number): number => {
      const n = Number(value);
      return Number.isFinite(n) ? n : fallback;
    };

    return {
      data,
      totalRecords: toNum(obj['totalRecords'], data.length),
      totalPages: toNum(obj['totalPages'], 1),
      currentPage: toNum(obj['currentPage'], requestedPage),
      pageSize: toNum(obj['pageSize'], requestedPageSize || data.length),
    };
  }

  getFacultyReferralFeedbackHistory(
    admNo: string,
    category: string = FACULTY_REFERRAL_CATEGORY
  ): Observable<FacultyReferralFeedbackHistoryEntry[]> {
    const adm = String(admNo ?? '').trim();
    const cat = encodeURIComponent(String(category ?? '').trim());
    const admParam = adm.includes('%') ? adm : encodeURIComponent(adm);
    const url = `${this.feedbackHistoryUrl}?admNo=${admParam}&category=${cat}`;
    return this.http
      .get<FacultyReferralFeedbackHistoryEntry[]>(url, { headers: this.headers })
      .pipe(map((list) => (Array.isArray(list) ? list : [])));
  }
  private unwrapStudentList(res: unknown): StudentSearchRowDto[] {
    if (Array.isArray(res)) return res as StudentSearchRowDto[];
    const o = res as Record<string, unknown> | null;
    if (!o) return [];
    const nested =
      o['data'] ??
      o['list'] ??
      o['studentList'] ??
      o['searchCacheList'] ??
      o['searchCacheDetails'] ??
      o['result'];
    if (Array.isArray(nested)) return nested as StudentSearchRowDto[];
    return [];
  }

  /**
   * Token for admNO / feedback APIs — not the display admission number.
   * Prefer encrypted adm fields; else stdUniqueRefId from cache/search row.
   */
  private resolveAdmNoToken(
    row: StudentSearchRowDto,
    admissionNo: string,
    stdUniqueRefId: string
  ): string {
    const encrypted = String(row.admNO ?? row.encryptedAdmNo ?? '').trim();
    if (encrypted) return encrypted;

    const adm = row.admNo != null ? String(row.admNo).trim() : '';
    if (adm && adm !== admissionNo) return adm;

    if (stdUniqueRefId) return stdUniqueRefId;

    return admissionNo;
  }

  private readStdUniqueRefId(row: StudentSearchRowDto): string {
    const raw = row as Record<string, unknown>;
    const v =
      row.stdUniqueRefId ??
      row.uniqueRefId ??
      raw['stdUniqueRefID'] ??
      raw['encryptedStdUniqueRefId'] ??
      raw['studentUniqueRefId'] ??
      raw['stdUniqueId'];
    return String(v ?? '').trim();
  }
 

  /** GET student/search/* — no JSON Content-Type (some gateways return 403 with it). */
  // private get getHeaders(): HttpHeaders {
  //   return new HttpHeaders({ Authorization: getEmployeeAuthorizationHeader() });
  // }

  // private get jsonHeaders(): HttpHeaders {
  //   return new HttpHeaders({
  //     'Content-Type': 'application/json',
  //     Authorization: getEmployeeAuthorizationHeader()
  //   });
  // }


}

export interface MappedStudentSearchRow {
  /** Display admission number */
  admissionNo: string;
  /** Encrypted / API token for admNo / admNO query and body */
  admNoToken: string;
  name: string;
  parentName: string;
  courseLine: string;
  campus: string;
  boardingType: string;
  /** Residential / Day scholar — from API studentOrienation / studentOrientation */
  studentOrientation: string;
  status: string;
  stdUniqueRefId: string;
  sectionId: number;
  /** Student photo from getStudentProfileInfo (if returned). */
  photoUrl?: string;
  raw: StudentSearchRowDto;
}
